import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { CodeUtility, EmbeddedCode, isClientPortal, jobViewPricingRights } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { CurrencyFormatPipe } from '@jms/utils/util.pipes';
import { injectWorkBuddyHost } from '@workbuddy/services/host/workbuddy.host.service';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { IStatusPanelAngularComp } from 'ag-grid-angular';
import { IStatusPanelParams } from 'ag-grid-enterprise';
import { itemSummaryStatusBarBridgeStyles, itemSummaryStatusBarStyleId } from './item.summary.status.bar.theme';

interface JobItemBoardRow {
  workOrders?: {
    codes?: EmbeddedCode;
  };
}

interface ItemSummary {
  itemCount: number;
  totalBuy: number;
  totalSell: number;
  profit: number;
  margin: number;
}

const emptySummary: ItemSummary = { itemCount: 0, totalBuy: 0, totalSell: 0, profit: 0, margin: 0 };

@Component({
  selector: 'item-summary-status-bar',
  standalone: true,
  templateUrl: './item.summary.status.bar.component.html',
  styleUrls: ['./item.summary.status.bar.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CurrencyFormatPipe],
  host: { role: 'status', 'aria-live': 'polite', 'aria-label': 'Item totals' },
})
export class ItemSummaryStatusBarComponent implements IStatusPanelAngularComp, OnDestroy {

  private readonly document = inject(DOCUMENT);
  private readonly currencyPipe = inject(CurrencyFormatPipe);
  private readonly rights = inject(RightsService);

  private readonly host = injectWorkBuddyHost();
  // A subcontractor holds no Jobs pricing right, yet the portal rows show their own buy price; the footer must total those.
  protected readonly contractor = this.host.isContractorPortal;
  // The client read sends sell only and the client holds no Jobs right, so its footer totals sell alone.
  protected readonly client = isClientPortal(this.host.domainType);
  private params: IStatusPanelParams<JobItemBoardRow> | null = null;
  private readonly onModelUpdated = () => this.updateSummary();

  protected readonly $summary = signal<ItemSummary>(emptySummary);
  // Prices are role-gated: the legacy items footer fell back to zero without ViewPricing, and the
  // board has no other gate on these fields, so the totals would otherwise leak them.
  protected readonly $canViewPricing = signal(false);
  protected readonly $totalBuy = computed(() => this.currencyPipe.transform(this.$canViewPricing() ? this.$summary().totalBuy : 0));
  protected readonly $totalSell = computed(() => this.currencyPipe.transform(this.$canViewPricing() ? this.$summary().totalSell : 0));
  protected readonly $profit = computed(() => this.currencyPipe.transform(this.$canViewPricing() ? this.$summary().profit : 0));
  // Falls back to a currency zero, not '0.00%', because that is what the legacy footer printed.
  protected readonly $margin = computed(() => this.$canViewPricing() ? `${formatNumber(this.$summary().margin)}%` : this.currencyPipe.transform(0));
  // Both cells take their colour from profit, never from margin, so a job cannot show a red profit
  // beside a green margin. Zero counts as a loss, and the colour ignores the pricing right, both
  // matching the legacy footer.
  protected readonly $isLoss = computed(() => this.$summary().profit <= 0);
  protected readonly $isProfit = computed(() => this.$summary().profit > 0);

  public constructor() {

    DesignSystem.Mount(this.document, itemSummaryStatusBarStyleId, itemSummaryStatusBarBridgeStyles);
  }

  public agInit(params: IStatusPanelParams<JobItemBoardRow>): void {

    this.bind(params);
  }

  public refresh(params: IStatusPanelParams<JobItemBoardRow>): boolean {

    this.bind(params);
    return true;
  }

  public ngOnDestroy(): void {

    this.params?.api.removeEventListener('modelUpdated', this.onModelUpdated);
  }

  private bind(params: IStatusPanelParams<JobItemBoardRow>): void {

    if (this.params?.api !== params.api) {
      this.params?.api.removeEventListener('modelUpdated', this.onModelUpdated);
      params.api.addEventListener('modelUpdated', this.onModelUpdated);
    }

    this.params = params;
    this.updateSummary();
  }

  private updateSummary(): void {

    const api = this.params?.api;
    if (!api) return;

    this.$canViewPricing.set(this.contractor || this.client || this.rights.hasRights(...jobViewPricingRights));

    let itemCount = 0;
    let totalBuy = 0;
    let totalSell = 0;

    api.forEachNodeAfterFilter(node => {
      if (node.group || node.detail || !node.data) return;

      const item = node.data.workOrders?.codes;
      if (!item) return;

      itemCount++;
      // The contractor total is the sum of the EXT figures its rows print, rounded the same way.
      totalBuy += this.contractor
        ? CodeUtility.getExtBuy(item)
        : resolveExtendedPrice(item.totalBuy, item.buyPrice, item.quantity);
      totalSell += resolveExtendedPrice(item.totalSellEx, item.sellPrice, item.quantity);
    });

    const profit = totalSell - totalBuy;
    const margin = totalSell ? profit / totalSell * 100 : 0;
    this.$summary.set({ itemCount, totalBuy, totalSell, profit, margin });
  }
}

function resolveExtendedPrice(total: number | null | undefined, unitPrice: number | null | undefined, quantity: number | null | undefined): number {

  if (Number.isFinite(total)) return Number(total);
  return toFiniteNumber(unitPrice) * toFiniteNumber(quantity);
}

function toFiniteNumber(value: number | null | undefined): number {

  return Number.isFinite(value) ? Number(value) : 0;
}

function formatNumber(value: number): string {

  return new Intl.NumberFormat('en', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value);
}
